import { createReducer, isAnyOf } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/fp/set'
import get from 'lodash/get'

import {
    changeOrganizationGroupRequest,
    changeOrganizationRequest,
    signOutSuccess,
} from 'actions/auth'
import { asyncReducerHydrated } from 'actions/ui/app'
import { FILTERS, FILTER_SETTINGS } from 'const/reducerKeys'
import { deepPick } from 'utilities/reducers'

import {
    getDefaultState,
    getPristineUiStateClone,
    updatePristineUiStateClone,
} from '../ui'

import { addDashboardCases } from './dashboard'
import { addDataManagementCases } from './dataManagement'
import {
    addFilterCases,
    makeDesiredFiltersVisible,
    updateFilters as resetFilters,
} from './filter'
import { addPageCases } from './page'
import { addResourceCases } from './resource'
import { SharedState } from './state'
import { addTabCases } from './tab'
import { addTableCases } from './table'

const initialState: SharedState = {}

export default createReducer(initialState, (builder) => {
    // reset all state except settings that are persisted across sessions
    builder.addCase(
        signOutSuccess,
        (state) =>
            deepPick(
                state,
                ['tableSettings', FILTERS, FILTER_SETTINGS],
                cloneDeep(getDefaultState())
            ) as SharedState
    )

    // when async reducers have been persisted and added
    builder.addCase(asyncReducerHydrated, (state, action) => {
        const { key, initialState: hydratedInitialState } = action.payload

        // update clean UI state with the new reducer state
        // so we can use for resetting
        const defaultUiStateClone = getPristineUiStateClone()
        const pageDefaultState = get(defaultUiStateClone, [key])
        if (!pageDefaultState) {
            const currentState = get(state, [key])
            updatePristineUiStateClone(hydratedInitialState ?? currentState, [
                key,
            ])
        }

        return set([key, 'hydrated'], true, state)
    })

    addPageCases(builder)
    addFilterCases(builder)
    addTabCases(builder)
    addDashboardCases(builder)
    addResourceCases(builder)
    addDataManagementCases(builder)
    addTableCases(builder)

    // on org change, don't reset filters since we could be redirecting to the correct org
    builder.addMatcher(
        isAnyOf(changeOrganizationRequest, changeOrganizationGroupRequest),
        (state, action) => {
            const { redirect, tab } = action.payload
            const { filters } = action.payload as {
                filters?: Record<string, unknown>
            }

            const copy: any = JSON.parse(
                JSON.stringify(
                    deepPick(
                        state,
                        ['tableSettings', FILTERS, FILTER_SETTINGS, 'hydrated'],
                        cloneDeep(getDefaultState())
                    )
                )
            )
            if ('app' in copy) {
                resetFilters(copy['app'].filters, filters)
                makeDesiredFiltersVisible(copy['app'].filtersSettings, filters)
            }
            if (redirect && redirect in copy) {
                let hasTouchedRedirectFilters = resetFilters(
                    copy[redirect]?.localFilters,
                    filters
                )
                hasTouchedRedirectFilters =
                    resetFilters(copy[redirect]?.filters, filters) ||
                    hasTouchedRedirectFilters
                const hasTouchedRedirectFilterSettings =
                    makeDesiredFiltersVisible(
                        copy[redirect].filtersSettings,
                        filters
                    )
                if (copy[redirect].filtersSettings) {
                    copy[redirect].filtersSettings.touched =
                        hasTouchedRedirectFilters ||
                        hasTouchedRedirectFilterSettings
                }
                if (tab && tab in copy[redirect]) {
                    let hasTouchedTabFilters = resetFilters(
                        copy[redirect][tab]?.filters,
                        filters
                    )
                    hasTouchedTabFilters =
                        resetFilters(
                            copy[redirect][tab]?.localFilters,
                            filters
                        ) || hasTouchedTabFilters
                    const hasTouchedTabFilterSettings =
                        makeDesiredFiltersVisible(
                            copy[redirect][tab].filtersSettings,
                            filters
                        )
                    if (copy[redirect][tab].filtersSettings) {
                        copy[redirect][tab].filtersSettings.touched =
                            hasTouchedTabFilters || hasTouchedTabFilterSettings
                    }
                }
                if (
                    tab &&
                    'tabs' in copy[redirect] &&
                    tab in copy[redirect].tabs
                ) {
                    let hasTouchedTabsFilters = resetFilters(
                        copy[redirect].tabs[tab]?.filters,
                        filters
                    )
                    hasTouchedTabsFilters =
                        resetFilters(
                            copy[redirect].tabs[tab]?.localFilters,
                            filters
                        ) || hasTouchedTabsFilters
                    const hasTouchedTabsFilterSettings =
                        makeDesiredFiltersVisible(
                            copy[redirect].tabs[tab].filtersSettings,
                            filters
                        )
                    if (copy[redirect].tabs[tab].filtersSettings) {
                        copy[redirect].tabs[tab].filtersSettings.touched =
                            hasTouchedTabsFilters ||
                            hasTouchedTabsFilterSettings
                    }
                }
            }

            return copy
        }
    )
})
